import { ColDef } from 'ag-grid-community';
import { ITransactionReportConfig } from '../models/transaction-report.models';
import { environment } from 'src/environments/environment';

const { defaultCurrency, defaultPageSizeArr, defaultPageSize } = environment;

/**
 * Formats a party type enum (e.g. CUSTOMER, MERCHANT) into a readable label
 */
function formatPartyType(value: string | null | undefined): string {
  if (!value) {
    return 'N/A';
  }
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

/**
 * Customer Transaction Report Configuration
 */
export const CUSTOMER_TRANSACTION_REPORT_CONFIG: ITransactionReportConfig = {
  reportType: 'CUSTOMER',
  title: 'Customer Transaction Report',
  subtitle: 'View and manage customer transactions',
  exportFileName: 'Customer_Transaction_Report',
  apiMethod: 'getTransactionReportListAll',
  defaultSort: 'DESC',
  defaultSortColumn: 'createDate',
  includeBackgroundExportFlag: false,

  columnDefs: [
    { field: 'userId', sortable: false, lockVisible: true, headerName: 'Sender ID', resizable: true },
    { field: 'userMobile', sortable: false, lockVisible: true, headerName: 'Sender Mobile' },
    { field: 'sender', sortable: false, lockVisible: true, headerName: 'Sender Name' },
    { field: 'receiverId', sortable: false, lockVisible: true, headerName: 'Receiver ID', valueGetter: (params) => params.data?.receiverId || 'N/A' },
    { field: 'receiverMobile', sortable: false, lockVisible: true, headerName: 'Receiver Mobile' },
    { field: 'receiverName', sortable: false, lockVisible: true, headerName: 'Receiver Name', valueGetter: (params) => params.data?.receiverName || 'N/A' },
    { field: 'receiverType', sortable: false, lockVisible: true, headerName: 'Receiver Type', valueGetter: (params) => params.data?.receiverType || 'N/A' },
    { field: 'txnId', sortable: false, lockVisible: true, headerName: 'Order ID' },
    { field: 'sendingCurrency', sortable: false, lockVisible: true, headerName: 'Send Currency' },
    { field: 'currency', sortable: false, lockVisible: true, headerName: 'Receive Currency' },
    { field: 'exchangeRate', sortable: false, lockVisible: true, headerName: 'FX Rate' },
    { field: 'amount', sortable: false, lockVisible: true, headerName: 'Amount' },
    { field: 'totalAmount', sortable: false, lockVisible: true, headerName: 'Total Amount' },
    { field: 'serviceCharges', sortable: false, lockVisible: true, headerName: 'Service Charges' },
    { field: 'vat', sortable: false, lockVisible: true, headerName: 'VAT' },
    { field: 'status', sortable: false, lockVisible: true, headerName: 'Status' },
    { field: 'serviceProviderResponseCode', sortable: false, lockVisible: true, headerName: 'SP Status' },
    { field: 'serviceProviderResponseMessage', sortable: false, lockVisible: true, headerName: 'SP Message' },
    {
      field: 'date', sortable: false, lockVisible: true, headerName: 'Transaction Date Time',
      valueFormatter: (params) => {
        const date = params.data?.date || '';
        const time = params.data?.time || '';
        return date && time ? `${date} ${time}` : date || time || '';
      }
    },
    { field: 'ipAddress', sortable: false, lockVisible: true, headerName: 'IP Address' },
    { field: 'gps', sortable: false, lockVisible: true, headerName: 'GPS Coordinates' },
    { field: 'osType', sortable: false, lockVisible: true, headerName: 'OS Type' },
    { field: 'osVersion', sortable: false, lockVisible: true, headerName: 'OS Version' },
    { field: 'deviceId', sortable: false, lockVisible: true, headerName: 'Device ID' },
    { field: 'appVersion', sortable: false, lockVisible: true, headerName: 'App Version' },
    { field: 'transactionType', sortable: false, lockVisible: true, headerName: 'Transaction Type' },
    { field: 'productCode', sortable: false, lockVisible: true, headerName: 'Product ID' },
    { field: 'channel', sortable: false, lockVisible: true, headerName: 'Channel' },
    { field: 'category', sortable: false, lockVisible: true, headerName: 'Category' },
    { field: 'initiatedFrom', sortable: false, lockVisible: true, headerName: 'Mode' },
    { field: 'referenceId', sortable: false, lockVisible: true, headerName: 'Reference ID' },
    { field: 'rrn', sortable: false, lockVisible: true, headerName: 'Bank Reference/RRN' }
  ],

  rowDataProcessor: (row: any) => {
    row.sendingCurrency = defaultCurrency;
    return row;
  }
};

/**
 * Merchant Transaction Report Configuration
 */
export const MERCHANT_TRANSACTION_REPORT_CONFIG: ITransactionReportConfig = {
  reportType: 'MERCHANT',
  title: 'Merchant Transaction Report',
  subtitle: 'View and manage merchant transactions',
  exportFileName: 'Merchant_Transaction_Report',
  apiMethod: 'getMerchantTransactionReport',
  defaultSort: 'DESC',
  defaultSortColumn: 'createDate',
  includeBackgroundExportFlag: true,

  columnDefs: [
    { field: 'receiverId', sortable: false, lockVisible: true, headerName: 'Merchant UUID' },
    { field: 'merchantIdentifierId', sortable: false, lockVisible: true, headerName: 'Merchant ID' },
    { field: 'receiverName', sortable: false, lockVisible: true, headerName: 'Receiver Name' },
    { field: 'userId', sortable: false, lockVisible: true, headerName: 'Customer ID' },
    { field: 'sender', sortable: false, lockVisible: true, headerName: 'Sender Name' },
    { field: 'userMobile', sortable: false, lockVisible: true, headerName: 'Customer Mobile' },
    { field: 'txnId', sortable: false, lockVisible: true, headerName: 'Transaction ID' },
    { field: 'transactionType', sortable: false, lockVisible: true, headerName: 'Credit / Debit' },
    { field: 'currency', sortable: false, lockVisible: true, headerName: 'Currency' },
    {
      field: 'amount', sortable: false, lockVisible: true, headerName: 'Sale Amount',
      valueFormatter: (params) => {
        return params.value != null ? Number(params.value).toFixed(2) : '';
      }
    },
    { field: 'serviceCharges', sortable: false, lockVisible: true, headerName: 'Fee Amount' },
    { field: 'vatOnCommission', sortable: false, lockVisible: true, headerName: 'VAT of Fee' },
    { field: 'receivingAmount', sortable: false, lockVisible: true, headerName: 'Net Amount' },
    { field: 'status', sortable: false, lockVisible: true, headerName: 'Status' },
    { field: 'dateTime', sortable: false, lockVisible: true, headerName: 'Trxn Initiated Date Time' },
    { field: 'updateDateTime', sortable: false, lockVisible: true, headerName: 'Updated Trxn Date Time' },
    { field: 'stan', sortable: false, lockVisible: true, headerName: 'STAN' },
    { field: 'ipAddress', sortable: false, lockVisible: true, headerName: 'IP Address' },
    { field: 'gps', sortable: false, lockVisible: true, headerName: 'GPS Coordinates' },
    { field: 'osType', sortable: false, lockVisible: true, headerName: 'OS Type' },
    { field: 'osVersion', sortable: false, lockVisible: true, headerName: 'OS Version' },
    { field: 'deviceModel', sortable: false, lockVisible: true, headerName: 'Device Model' },
    { field: 'deviceId', sortable: false, lockVisible: true, headerName: 'Device ID' },
    { field: 'originalTxnId', headerName: 'Original TRXN ID', lockVisible: true },
    { field: 'appVersion', sortable: false, lockVisible: true, headerName: 'App Version' },
    { field: 'typeOfTransaction', sortable: false, lockVisible: true, headerName: 'Txn Type' },
    { field: 'productCode', sortable: false, lockVisible: true, headerName: 'Product Code' },
    { field: 'channel', sortable: false, lockVisible: true, headerName: 'Channel' },
    { field: 'category', sortable: false, lockVisible: true, headerName: 'Category' },
    { field: 'initiatedFrom', sortable: false, lockVisible: true, headerName: 'Mode' },
    { field: 'referenceId', sortable: false, lockVisible: true, headerName: 'Pay10 Reference ID' },
    { field: 'splitPayment', sortable: false, lockVisible: true, headerName: 'Split Payment' },
    { field: 'instantCapture', sortable: false, lockVisible: true, headerName: 'Instant Captured' },
    { field: '', sortable: false, lockVisible: true, headerName: 'Outlet ID' },
    { field: 'merchantTerminalId', sortable: false, lockVisible: true, headerName: 'Terminal ID' },
    { field: 'commissionPercentage', sortable: false, lockVisible: true, headerName: 'Commission' },
    { field: 'rrn', sortable: false, lockVisible: true, headerName: 'Bank Reference/RRN' }
  ],

  rowDataProcessor: (row: any) => {
    row.splitPayment = row.splitPayment || 'N';
    return row;
  }
};

/**
 * All Transaction Report Configuration
 * Maps to the backend `AllTransactionResponseDTO` / GET /transaction/transaction-report/all
 */
export const ALL_TRANSACTION_REPORT_CONFIG: ITransactionReportConfig = {
  reportType: 'MERCHANT',
  title: 'All Transactions Report',
  subtitle: 'View all wallet transactions',
  exportFileName: 'All_Transaction_Report',
  apiMethod: 'getTransactionList',
  defaultSort: 'DESC',
  defaultSortColumn: 'date',

  columnDefs: [
    { field: 'txnId', sortable: false, lockVisible: true, headerName: 'Transaction ID', valueGetter: (params) => params.data?.txnId || 'N/A' },
    { field: 'externalId', sortable: false, lockVisible: true, headerName: 'External ID', valueGetter: (params) => params.data?.externalId || 'N/A' },
    {
      field: 'date', sortable: false, lockVisible: true, headerName: 'Date',
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : 'N/A'
    },
    { field: 'productCode18n', sortable: false, lockVisible: true, headerName: 'Txn Type', valueGetter: (params) => params.data?.productCode18n || 'N/A' },
    { field: 'transactionType', sortable: false, lockVisible: true, headerName: 'Credit / Debit', valueGetter: (params) => params.data?.transactionType || 'N/A' },
    { field: 'status', sortable: false, lockVisible: true, headerName: 'Status', valueGetter: (params) => params.data?.status || 'N/A' },
    { field: 'currency', sortable: false, lockVisible: true, headerName: 'Currency', valueGetter: (params) => params.data?.currency || 'N/A' },
    {
      field: 'onUs', sortable: false, lockVisible: true, headerName: 'On Us',
      valueFormatter: (params) => params.value === true ? 'Yes' : params.value === false ? 'No' : 'N/A'
    },
    {
      field: 'amount', sortable: false, lockVisible: true, headerName: 'Amount',
      valueFormatter: (params) => params.value != null ? Number(params.value).toFixed(2) : 'N/A'
    },
    {
      field: 'exchangeRate', sortable: false, lockVisible: true, headerName: 'FX Rate',
      valueFormatter: (params) => params.value != null ? Number(params.value).toFixed(2) : 'N/A'
    },
    {
      field: 'serviceCharges', sortable: false, lockVisible: true, headerName: 'Service Charges',
      valueFormatter: (params) => params.value != null ? Number(params.value).toFixed(2) : 'N/A'
    },
    {
      field: 'commissionPercentage', sortable: false, lockVisible: true, headerName: 'Commission %',
      valueFormatter: (params) => params.value != null ? Number(params.value).toFixed(2) : 'N/A'
    },
    {
      field: 'commissionAmount', sortable: false, lockVisible: true, headerName: 'Commission Amount',
      valueFormatter: (params) => params.value != null ? Number(params.value).toFixed(2) : 'N/A'
    },
    {
      field: 'vatOnCommission', sortable: false, lockVisible: true, headerName: 'VAT on Commission',
      valueFormatter: (params) => params.value != null ? Number(params.value).toFixed(2) : 'N/A'
    },
    {
      field: 'receivingAmount', sortable: false, lockVisible: true, headerName: 'Receiving Amount',
      valueFormatter: (params) => params.value != null ? Number(params.value).toFixed(2) : 'N/A'
    },
    { field: 'stan', sortable: false, lockVisible: true, headerName: 'STAN', valueGetter: (params) => params.data?.stan || 'N/A' },
    { field: 'senderName', sortable: false, lockVisible: true, headerName: 'Sender Name', valueGetter: (params) => params.data?.senderName || 'N/A' },
    { field: 'senderMobileNumber', sortable: false, lockVisible: true, headerName: 'Sender Mobile', valueGetter: (params) => params.data?.senderMobileNumber || 'N/A' },
    {
      field: 'senderType', sortable: false, lockVisible: true, headerName: 'Sender Type',
      valueFormatter: (params) => formatPartyType(params.value)
    },
    { field: 'payerWalletType', sortable: false, lockVisible: true, headerName: 'Payer Wallet Type', valueGetter: (params) => params.data?.payerWalletType || 'N/A' },
    { field: 'senderRib', sortable: false, lockVisible: true, headerName: 'Sender RIB', valueGetter: (params) => params.data?.senderRib || 'N/A' },
    {
      field: 'payerOpeningBalance', sortable: false, lockVisible: true, headerName: 'Payer Opening Balance',
      valueFormatter: (params) => params.value != null ? Number(params.value).toFixed(2) : 'N/A'
    },
    {
      field: 'payerClosingBalance', sortable: false, lockVisible: true, headerName: 'Payer Closing Balance',
      valueFormatter: (params) => params.value != null ? Number(params.value).toFixed(2) : 'N/A'
    },
    { field: 'receiverName', sortable: false, lockVisible: true, headerName: 'Receiver Name', valueGetter: (params) => params.data?.receiverName || 'N/A' },
    { field: 'receiverMobileNumber', sortable: false, lockVisible: true, headerName: 'Receiver Mobile', valueGetter: (params) => params.data?.receiverMobileNumber || 'N/A' },
    {
      field: 'receiverType', sortable: false, lockVisible: true, headerName: 'Receiver Type',
      valueFormatter: (params) => formatPartyType(params.value)
    },
    { field: 'receiverWalletType', sortable: false, lockVisible: true, headerName: 'Receiver Wallet Type', valueGetter: (params) => params.data?.receiverWalletType || 'N/A' },
    { field: 'receiverRib', sortable: false, lockVisible: true, headerName: 'Receiver RIB', valueGetter: (params) => params.data?.receiverRib || 'N/A' },
    {
      field: 'receiverOpeningBalance', sortable: false, lockVisible: true, headerName: 'Receiver Opening Balance',
      valueFormatter: (params) => params.value != null ? Number(params.value).toFixed(2) : 'N/A'
    },
    {
      field: 'receiverClosingBalance', sortable: false, lockVisible: true, headerName: 'Receiver Closing Balance',
      valueFormatter: (params) => params.value != null ? Number(params.value).toFixed(2) : 'N/A'
    }
  ]
};

/**
 * Status filter options for transaction reports
 */
export const TRANSACTION_STATUS_OPTIONS = [
  { label: 'Success', value: 'SUCCESS' },
  { label: 'Failed', value: 'FAILED' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Request Initiated', value: 'REQUEST_INITIATED' },
  { label: 'Refund', value: 'REFUNDED' },
  { label: 'Reversed', value: 'REVERSED' }
];

/**
 * On Us filter options for transaction reports
 */
export const TRANSACTION_ON_US_OPTIONS = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' }
];

/**
 * Party type filter options (used for Sender Type / Receiver Type) for transaction reports
 */
export const TRANSACTION_PARTY_TYPE_OPTIONS = [
  { label: 'Customer', value: 'CUSTOMER' },
  { label: 'Merchant', value: 'MERCHANT' }
];

/**
 * Transaction type filter options (backend `ProductCode18n` enum values)
 */
export const TRANSACTION_PRODUCT_CODE_OPTIONS = [
  { label: 'Card Topup', value: 'CARD_TOPUP' },
  { label: 'Cash Topup', value: 'CASH_TOPUP' },
  { label: 'Merchant Topup', value: 'MERCHANT_TOPUP' },
  { label: 'Bank To Wallet', value: 'BANK_TO_WALLET' },
  { label: 'Wallet To Bank', value: 'WALLET_TO_BANK' },
  { label: 'Fatourati Topup', value: 'FATOURATI_TOPUP' },
  { label: 'Billpay', value: 'BILLPAY' },
  { label: 'Bulk Transfer', value: 'BULK_TRANSFER' },
  { label: 'QR PM On Us', value: 'QR_PM_ON_US' },
  { label: 'QR PM Off Us In', value: 'QR_PM_OFF_US_IN' },
  { label: 'QR PM Off Us Out', value: 'QR_PM_OFF_US_OUT' },
  { label: 'MDN PM On Us', value: 'MDN_PM_ON_US' },
  { label: 'MDN PM Off Us In', value: 'MDN_PM_OFF_US_IN' },
  { label: 'MDN PM Off Us Out', value: 'MDN_PM_OFF_US_OUT' },
  { label: 'Own Mobile Topup', value: 'OWN_MOBILE_TOPUP' },
  { label: 'Other Mobile Topup', value: 'OTH_MOBILE_TOPUP' },
  { label: 'P2C Cash-Out', value: 'P2C_CASH_OUT' },
  { label: 'Cash-Out', value: 'CASH_OUT' },
  { label: 'Cash-Out GAB', value: 'CASH_OUT_GAB' },
  { label: 'P2P On Us', value: 'P2P_ON_US' },
  { label: 'P2P Off-Us In', value: 'P2P_OFF_US_IN' },
  { label: 'P2P Off-Us Out', value: 'P2P_OFF_US_OUT' },
  { label: 'QR P2P On Us', value: 'QR_P2P_ON_US' },
  { label: 'QR P2P Off-Us In', value: 'QR_P2P_OFF_US_IN' },
  { label: 'QR P2P Off-Us Out', value: 'QR_P2P_OFF_US_OUT' },
  { label: 'IRCV', value: 'IRCV' },
  { label: 'Rollback', value: 'ROLLBACK' }
];
